import turf from '@turf/turf';

import { featureGetZoom } from './geojson-utilities';

/**
 * @typedef {object} Feature
 * @typedef {object} Point
 */

const { PI } = Math;
const DEG_TO_RAD = PI / 180;

/**
 * Transforms the coordinates and zoom of a feature point into a XYZ object.
 *
 * @param {Feature<Point>} featurePoint - A feature point with a given position.
 * @returns {object} An object with the XYZ values.
 */
export const coordinatesToXYZ = featurePoint => {
  const [lng, lat] = featurePoint.geometry.coordinates;
  const zoom = featureGetZoom(featurePoint);
  // eslint-disable-next-line no-bitwise
  const bitWise = 1 << zoom;
  return {
    x: Math.floor(((lng + 180) / 360) * bitWise),
    y: Math.floor(
      ((1 - Math.log(Math.tan(lat * DEG_TO_RAD) + 1 / Math.cos(lat * DEG_TO_RAD)) / Math.PI) / 2) *
        bitWise
    ),
    z: zoom
  };
};

/**
 * Transforms the XYZ object into a feature point with coordinates and zoom.
 *
 * @param {object} arg - An object with the XYZ values.
 * @param {number} arg.x - The X value.
 * @param {number} arg.y - The Y value.
 * @param {number} arg.z - The Z value.
 * @returns {Feature<Point>} A feature point with a given position.
 */
export const xyzToCoordinates = ({ x, y, z }) => {
  // eslint-disable-next-line no-bitwise
  const bitWise = 1 << z;
  const lng = (x / bitWise) * 360 - 180;
  const lat = Math.atan(Math.sinh(Math.PI - (2 * Math.PI * y) / bitWise)) / DEG_TO_RAD;
  return turf.point([lng, lat], { zoom: z });
};
